import InfiniteScroll from "react-infinite-scroll-component";
import MyMessage from "./MyMessage";
import { formatUTCtoLocalDate } from "../../../utils/formatUTCtoLocalDate";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import LoadMoreMessages from "./LoadMoreMessages";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import scrollToMessage from "../../../utils/scrollToMessage";
import SystemMessage from "./SystemMessage";
import { useLocation } from "react-router-dom";
import { useMessageList } from "../../../socket/useMessageList";
import { updateMessageList } from "../../../store/Slices/MessageListSlice";
import type { MessageListRes } from "../../../types/MessageListType";
import {
  PIN_BAR_HEIGHT,
  getNewestMessageId,
  getOldestMessageId,
  hasPendingMessageListScroll,
  restoreMessageListScroll,
  startDeepHistoryLayoutGuard,
  startMessageListMediaScrollGuard,
  stopDeepHistoryLayoutGuard,
} from "../../../utils/messageListWindow";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { groupMessagesForView } from "../../../utils/groupMessagesForView";
import MediaAlbumBubble from "./MediaAlbumBubble";
import { useTheme } from "../../../context/ThemeProvider";
import SelectedMessageOption from "./SelectedMessageOption/SelectedMessageOption";
import ThanosSnap from "../../../components/ThanosSnap";
import { finishMessageSnap } from "../../../store/Slices/MessageListSlice";

export default function MessageBody() {
  const location = useLocation();
  const translate = useTranslateText();
  const { locale } = useTimeFormatOptions();
  const MessageListArray = useAppSelector((state) => state.MessageList);
  const NavigateTospesificMessage = useAppSelector(
    (state) => state.NavigateToSpesificMessage,
  );
  const messageListDetails = useAppSelector((state) => state.MessageOptions);
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const pinCount = useAppSelector(
    (state) => state.MessageList.pinned_messages.Records.length,
  );
  const dispatch = useAppDispatch();
  const [isIOS, setIsIOS] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectModeRef = useRef(messageListDetails.selectMessage);
  const isVideoCallRoute = location.pathname === "/video-call";
  // @ts-ignore
  const { theme } = useTheme();
  const userData = useAppSelector((s) => s.userData);
  const myUserId = userData.user_id;
  const snappingIds = useAppSelector(
    (s) => s.MessageList.snappingIds || [],
  );
  const listEntries = groupMessagesForView(
    MessageListArray.message_list?.Records || [],
  );

  // Window trim can keep Records.length unchanged (+N older / −N newer).
  // Depend on edges too so anchor restore still runs after tip trim.
  const windowOldestId = getOldestMessageId(
    MessageListArray.message_list?.Records || [],
  );
  const windowNewestId = getNewestMessageId(
    MessageListArray.message_list?.Records || [],
  );

  // Restore scroll BEFORE paint after older/newer packs, then
  // keep the same visual anchor while media finishes decoding.
  useLayoutEffect(() => {
    if (!hasPendingMessageListScroll()) return;
    restoreMessageListScroll(listRef.current);
    startMessageListMediaScrollGuard(listRef.current);
  }, [
    MessageListArray.message_list?.Records?.length,
    windowOldestId,
    windowNewestId,
  ]);

  // Select / forward mode must not jump the conversation (flex-col-reverse)
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (selectModeRef.current === messageListDetails.selectMessage) return;
    selectModeRef.current = messageListDetails.selectMessage;
    const top = el.scrollTop;
    el.scrollTop = top;
  }, [messageListDetails.selectMessage]);

  // Mid-history: pin viewport when link OG / images expand after paint
  useEffect(() => {
    if (!messageListDetails.has_more_newer) {
      stopDeepHistoryLayoutGuard();
      return;
    }
    const cleanup = startDeepHistoryLayoutGuard(listRef.current);
    return () => {
      cleanup?.();
      stopDeepHistoryLayoutGuard();
    };
  }, [
    messageListDetails.has_more_newer,
    currentConversationData.chat_id,
  ]);

  const { data: videoCallListPayload } = useMessageList<MessageListRes>(
    currentConversationData.chat_id,
    currentConversationData.user_id!,
    1,
  );

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
  }, []);

  useEffect(() => {
    if (!isVideoCallRoute) return;
    if (messageListDetails.historyMode === "window") return;

    if (videoCallListPayload?.message_list?.Records?.length) {
      const tipChatId = videoCallListPayload.message_list.Records.find(
        (m) => m.message_type !== "date" && m.chat_id != null,
      )?.chat_id;
      if (
        tipChatId != null &&
        Number(tipChatId) !== Number(currentConversationData.chat_id)
      ) {
        return;
      }
      dispatch(updateMessageList(videoCallListPayload));
      setTimeout(() => {
        scrollToMessage(
          videoCallListPayload.message_list?.Records?.[0]?.message_id!,
          false,
          false,
        );
      }, 10);
      const pagination = videoCallListPayload.message_list!.Pagination!;
      dispatch(
        updateMessageOptions({
          totalPages: Number(pagination.total_pages),
          isMessageLoading: false,
          isMoreMessageLoading: false,
          messageListAtTop: false,
          historyMode: "page",
          has_more_older:
            pagination.has_more_older ??
            Number(pagination.current_page) < Number(pagination.total_pages),
          has_more_newer: false,
        }),
      );
      return;
    }

    if (currentConversationData.chat_id <= 0) {
      dispatch(updateMessageOptions({ isMessageLoading: false }));
    } else if (currentConversationData.is_message_list_empty) {
      dispatch(updateMessageOptions({ isMessageLoading: false }));
    }
  }, [
    isVideoCallRoute,
    videoCallListPayload,
    currentConversationData.chat_id,
    currentConversationData.is_conversation_changed,
    currentConversationData.is_message_list_empty,
    messageListDetails.historyMode,
    dispatch,
  ]);

  const fetchMoreMessages = () => {
    if (
      messageListDetails.has_more_older &&
      !messageListDetails.isMoreMessageLoading
    ) {
      dispatch(updateMessageOptions({ messageListAtTop: true }));
    }
  };

  useEffect(() => {
    if (
      MessageListArray.message_list?.Records?.length !== 0 &&
      NavigateTospesificMessage.navigate_to_message === false
    ) {
      if (
        messageListDetails.historyMode === "page" &&
        (messageListDetails.currentPage === "1" ||
          messageListDetails.currentPage === "")
      ) {
        const lastMsg =
          MessageListArray.message_list.Records[
            MessageListArray.message_list.Records.length - 1
          ];
        if (lastMsg) {
          scrollToMessage(lastMsg.message_id, false, false);
        }
      }
    }
  }, []);

  // Pad so last bubble can sit above compose; list itself fills under the input overlay
  const listPadBottom = isVideoCallRoute
    ? isIOS
      ? "pb-12"
      : "pb-8"
    : isIOS
      ? "pb-[7.5rem]"
      : "pb-[6.75rem]";

  // Pin bar overlays the list; pad the physical top so oldest messages aren't hidden
  const pinPadTop = pinCount > 0 ? PIN_BAR_HEIGHT + 8 : 0;

  return (
    <div
      ref={listRef}
      id="messageListDiv"
      style={{
        ...(pinPadTop ? { paddingTop: pinPadTop } : null),
        // Prevent browser scroll-anchoring from fighting our useLayoutEffect restore
        overflowAnchor: "none",
        // iOS/Safari: avoid 100vh jump; parent flex gives real height
        WebkitOverflowScrolling: "touch",
      }}
      className={`flex h-full min-h-0 w-full flex-col-reverse gap-y-2 overflow-y-auto overflow-x-hidden ${!isVideoCallRoute ? "sm:px-14 lg:px-20" : "px-2 lg:px-0"} ${listPadBottom} lg:pt-6`}
    >
      <LoadMoreMessages />
      <InfiniteScroll
        dataLength={MessageListArray.message_list?.Records?.length || 0}
        next={fetchMoreMessages}
        // Prefetch owns loading; InfiniteScroll is a last-resort trigger only.
        // Keeping threshold high avoids "pack lands → still at top → next pack" rockets.
        // Prefetch owns loading. Keep InfiniteScroll as a rare fallback only
        // while NOT already loading — never auto-chain packs at the top.
        hasMore={
          messageListDetails.has_more_older &&
          !messageListDetails.isMoreMessageLoading
        }
        inverse={true}
        scrollThreshold={0.05}
        loader={<span className="sr-only">Loading older messages</span>}
        scrollableTarget="messageListDiv"
        // Kill nested scroller — only #messageListDiv scrolls (avoids flash)
        style={{ overflow: "visible", height: "auto" }}
      >
        {listEntries.map((entry) => {
          if (entry.kind === "group") {
            const head = entry.messages[0];
            const last =
              entry.messages[entry.messages.length - 1] || head;
            const isOwn =
              myUserId != null &&
              head?.sender_id != null &&
              String(head.sender_id) === String(myUserId);
            const sender = head?.User;
            const avatarSrc =
              sender?.profile_pic ||
              (isOwn ? userData.profile_pic : "") ||
              "";
            return (
              <div
                key={`g-${entry.grouped_id}`}
                id={String(last.message_id)}
                data-message-id={last.message_id}
                className="relative my-2 flex flex-col rounded-lg"
              >
                <div className="flex items-end justify-end">
                  <div
                    className={`mx-2 flex w-full flex-col space-y-2 ${
                      isOwn ? "order-1 items-end" : "order-2"
                    }`}
                  >
                    <SelectedMessageOption
                      messageData={last}
                      disabled={!!messageListDetails.selectMessage}
                    >
                      <ThanosSnap
                        active={entry.messages.some((m) =>
                          snappingIds.includes(m.message_id),
                        )}
                        onSettled={() =>
                          dispatch(
                            finishMessageSnap(
                              entry.messages.map((m) => m.message_id),
                            ),
                          )
                        }
                      >
                        <MediaAlbumBubble
                          messages={entry.messages}
                          isOwn={isOwn}
                          theme={theme}
                        />
                      </ThanosSnap>
                    </SelectedMessageOption>
                  </div>
                  <img
                    src={avatarSrc}
                    alt=""
                    className={`mt-auto h-7 w-7 shrink-0 rounded-full object-cover ${
                      isOwn ? "order-2 mb-2" : "order-1 mb-2"
                    }`}
                  />
                </div>
              </div>
            );
          }

          const e = entry.message;
          if (e.message_type === "date") {
            return (
              <div
                key={e.message_id}
                id={String(e.message_id)}
                className="mx-auto my-3 w-fit rounded-full bg-messageHead px-5 py-1 text-center text-sm font-medium text-darkText shadow-sm"
              >
                {new Date(e.createdAt).toDateString() ===
                new Date().toDateString()
                  ? translate("Today")
                  : new Date(e.createdAt).toDateString() ===
                      new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString()
                    ? translate("Yesterday")
                    : formatUTCtoLocalDate(e.createdAt, locale)}
              </div>
            );
          } else if (
            [
              "group-created",
              "member-added",
              "member-removed",
              "promoted-as-admin",
              "removed-as-admin",
              "member-left",
              "block",
            ].includes(e.message_type)
          ) {
            return (
              <SystemMessage key={e.message_id} messageData={e} />
            );
          } else {
            return (
              <MyMessage key={e.message_id} messageData={e} />
            );
          }
        })}
      </InfiniteScroll>
    </div>
  );
}
